import { detectLanguageIso1 } from '../shared/lang';
import { IMAGE_RESOLUTION_PRESETS } from './resolutions';

/**
 * 通用尺寸备注映射（作为各模型的默认备注，可被模型自定义覆盖）
 */
export const defaultSizeNotes: Record<string, string> = {
  match: '(Auto)',
  '4096x4096': '(1:1)',
  '3840x2160': '(16:9)',
  '3136x1344': '(21:9)',
  '2752x1536': '(16:9)',
  '2496x1664': '(3:2)',
  '2368x1792': '(4:3)',
  '2368x1664': '(4:3)',
  '2304x1856': '(5:4)',
  '2048x2048': '(1:1)',
  '2048x768': '(21:9)',
  '1856x2304': '(4:5)',
  '1792x2368': '(3:4)',
  '1664x2496': '(2:3)',
  '1536x2752': '(9:16)',
  '1536x1024': '(3:2)',
  '1344x3136': '(9:21)',
  '1344x1024': '(4:3)',
  '1280x1024': '(5:4)',
  '1024x2048': '(1:2)',
  '1024x1536': '(2:3)',
  '1024x1344': '(3:4)',
  '1024x1280': '(4:5)',
  '1024x1024': '(1:1)',
  '832x1440': '(9:16)',
  '768x2048': '(9:21)',
  '1440x832': '(16:9)',
  '1792x1024': '(7:4)',
  '1024x1792': '(4:7)',
};

/**
 * 根据 content-type 推断图片文件扩展名
 */
export function inferImageExtension(contentType: string): string {
  const type = contentType.toLowerCase();
  if (type.includes('jpeg') || type.includes('jpg')) return 'jpg';
  if (type.includes('webp')) return 'webp';
  if (type.includes('gif')) return 'gif';
  return 'png';
}

/**
 * 下载远程图片并构建 multipart/form-data 请求体（图片编辑类接口通用）
 * @param fields 文本字段（值为 undefined 的字段会被跳过，等效于不传该参数）
 * @param imageUrls 输入图片 URL 列表（会被下载为 Blob 以文件形式上传）
 * @param fileFieldName 图片文件字段名，默认 'image[]'
 */
export async function buildImageFormData(
  fields: Record<string, string | undefined>,
  imageUrls: string[],
  fileFieldName = 'image[]'
): Promise<FormData> {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined) {
      form.append(key, value);
    }
  }
  const blobs = await Promise.all(
    imageUrls.map(async (url) => {
      const resp = await fetch(url);
      if (!resp.ok) {
        throw new Error(`Failed to fetch input image (${resp.status}): ${url}`);
      }
      return await resp.blob();
    })
  );
  blobs.forEach((blob, index) => {
    const ext = inferImageExtension(blob.type || '');
    form.append(fileFieldName, blob, `image_${index}.${ext}`);
  });
  return form;
}

/**
 * 判断是否有图像输入（类型守卫）
 * @param imageUrls 图像 URL 数组
 * @returns 是否有图像输入，如果为 true 则 imageUrls 一定存在且非空
 */
export function hasImageInput(imageUrls?: string[]): imageUrls is string[] {
  return !!(imageUrls && imageUrls.length > 0);
}

/**
 * 从 providerOptions 中提取图像输入 URL 数组
 * @param providerOptions 提供者选项
 * @returns 图像 URL 数组，如果没有则返回 undefined
 */
export function extractImageUrls(providerOptions?: Record<string, any>): string[] | undefined {
  return (providerOptions as any)?.url?.image_input;
}

/**
 * 提取第一个图像 URL
 * @param imageUrls 图像 URL 数组
 * @returns 第一个图像 URL，如果没有则返回 undefined
 */
export function extractFirstImageUrl(imageUrls?: string[]): string | undefined {
  return imageUrls && imageUrls.length > 0 ? imageUrls[0] : undefined;
}

/**
 * 将尺寸字符串映射为比例字符串
 * @param size 尺寸字符串，例如 '1024x1024'
 * @returns 比例字符串，例如 '1:1'，如果未匹配则返回 '1:1'
 */
export function mapSizeToRatio(size?: string): string {
  const ratioMap: Record<string, string> = {
    '1672x941': '16:9',
    '941x1672': '9:16',
    '1024x1024': '1:1',
    '1024x1536': '2:3',
    '1536x1024': '3:2',
  };
  return ratioMap[size ?? ''] ?? '1:1';
}

/**
 * 将比例字符串映射为尺寸字符串（mapSizeToRatio 的反向映射）
 * @param ratio 比例字符串，例如 '16:9'
 * @returns 尺寸字符串，例如 '1672x941'，如果未匹配则返回 '1024x1024'
 */
export function mapRatioToSize(ratio?: string): string {
  const sizeMap: Record<string, string> = {
    '16:9': '1672x941',
    '9:16': '941x1672',
    '1:1': '1024x1024',
    '2:3': '1024x1536',
    '3:2': '1536x1024',
  };
  return sizeMap[ratio ?? ''] ?? '1024x1024';
}

/**
 * 约束 value 的值在 allowedValues 中
 */
export function clampTo<T extends string | number>(
    value: T,
    allowedValues: readonly T[],
    defaultValue: T
): T {
    return allowedValues.includes(value) ? value : defaultValue;
}

/**
 * 判断提示词语言
 * @param prompt 提示词文本
 * @returns ISO 639-1 语言代码（如 'zh', 'en'），如果无法检测则返回 undefined
 */
export function detectPromptLanguage(prompt?: string): string | undefined {
    if (!prompt || typeof prompt !== 'string') {
        return undefined;
    }
    
    const cleanPrompt = prompt.trim();
    if (cleanPrompt.length === 0) {
        return undefined;
    }
    
    // 使用简单的字符检测作为快速判断
    // 检测中文字符（CJK统一表意文字）
    const chinesePattern = /[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff]/;
    if (chinesePattern.test(cleanPrompt)) {
        return 'zh';
    }
    
    // 检测日文字符
    const japanesePattern = /[\u3040-\u309f\u30a0-\u30ff]/;
    if (japanesePattern.test(cleanPrompt)) {
        return 'ja';
    }
    
    // 检测韩文字符
    const koreanPattern = /[\uac00-\ud7af]/;
    if (koreanPattern.test(cleanPrompt)) {
        return 'ko';
    }
    
    // 如果没有检测到CJK字符，使用语言检测库进行更精确的检测
    return detectLanguageIso1(cleanPrompt);
}

/**
 * 判断提示词是否为中文
 * @param prompt 提示词文本
 * @returns 是否为中文
 */
export function isChinesePrompt(prompt?: string): boolean {
    return detectPromptLanguage(prompt) === 'zh';
}

/**
 * 判断提示词是否为英文
 * @param prompt 提示词文本
 * @returns 是否为英文
 */
export function isEnglishPrompt(prompt?: string): boolean {
    const lang = detectPromptLanguage(prompt);
    return lang === 'en' || lang === undefined; // undefined 时默认视为英文
}

/**
 * 判断提示词是否需要翻译（非英文需要翻译）
 * @param prompt 提示词文本
 * @returns 是否需要翻译
 */
export function needsTranslation(prompt?: string): boolean {
    const lang = detectPromptLanguage(prompt);
    return lang !== undefined && lang !== 'en';
}

/**
 * 校验 prompt 中已存在合法的 MJ 风格 aspect ratio 参数
 * 例如：`cat --ar 16:9` 或 `cat --aspect 16:9`
 */
const VALID_MJ_ASPECT_RATIO_RE =
  /(^|\s)--(?:ar|aspect)\s+\d+:\d+(?=\s|$)/i;

/**
 * 修正常见 MJ aspect ratio 参数误写，统一为 `--ar <ratio>` 或 `--aspect <ratio>` 形式
 *
 * 修正示例：
 *   cat--aspect16:9    -> cat --aspect 16:9
 *   cat--aspect 16:9   -> cat --aspect 16:9
 *   cat --aspect16:9   -> cat --aspect 16:9
 *   cat -- aspect16:9  -> cat --aspect 16:9
 *   cat -- aspect 16:9 -> cat --aspect 16:9
 *   cat --aspect=16:9  -> cat --aspect 16:9
 *   cat -- aspect=16:9 -> cat --aspect 16:9
 *
 * 不修正（用户未显式写 `--` 的情况）：
 *   cat aspect 16:9
 *   cat ar 16:9
 *   cat -- ratio 16:9
 */
export function normalizeMJAspectRatioParam(prompt: string): string {
  return prompt.replace(
    /(^|\s|\S)--\s*(ar|aspect)\s*(?:=|\s)?\s*(\d+:\d+)(?=\s|$)/gi,
    (_match, prefix: string, key: string, ratio: string) => {
      const safePrefix = prefix && /\s/.test(prefix) ? prefix : `${prefix} `;
      return `${safePrefix}--${key.toLowerCase()} ${ratio}`;
    }
  );
}

/**
 * 为 prompt 追加 MJ 风格 `--ar <ratio>` 参数（若已存在合法参数则不追加）
 * 会先调用 `normalizeMJAspectRatioParam` 修正常见误写。
 *
 * @param prompt 用户输入的提示词
 * @param aspectRatio 期望的比例（若未提供或不在 allowed 列表中则使用 defaultRatio）
 * @param allowedRatios 允许的比例集合
 * @param defaultRatio 默认比例（兜底值，必须在 allowedRatios 中）
 */
export function appendMJAspectRatio<T extends string>(
  prompt: string,
  aspectRatio: string | undefined,
  allowedRatios: readonly T[],
  defaultRatio: T
): string {
  const normalized = normalizeMJAspectRatioParam(prompt ?? '').trim();

  if (VALID_MJ_ASPECT_RATIO_RE.test(normalized)) {
    return normalized;
  }

  const safe = clampTo(
    (aspectRatio || defaultRatio) as T,
    allowedRatios,
    defaultRatio
  );

  return normalized ? `${normalized} --ar ${safe}` : `--ar ${safe}`;
}

/**
 * 剥离 prompt 中指定的 MJ 风格参数（如 `--hd`、`--v 6`、`--version 6.1`）
 *
 * - 同时兼容布尔型（`--hd`）与带值型（`--v 6`、`--version=6.1`）参数
 * - 大小写不敏感
 * - 多余空格会被折叠
 *
 * 用途：服务端最终决定的参数（如分辨率、模型版本）必须以服务端选择为准，
 * 防止用户在 prompt 中手动添加参数绕过计费 / 限制。
 *
 * @example
 *   stripMJFlags('cat --hd --v 6 --ar 16:9', ['hd', 'v', 'version'])
 *   // => 'cat --ar 16:9'
 */
export function stripMJFlags(prompt: string, flags: readonly string[]): string {
  if (!prompt || flags.length === 0) {
    return prompt ?? '';
  }
  const keys = flags.map((f) => f.replace(/^-+/, '')).join('|');
  // 匹配 `--key`，可选 `=value` 或 ` value`
  // 空格分隔的 value 只匹配“像参数值”的 token（数字 / 版本号 / 比例），
  // 避免布尔型参数（如 `--fast cat`）误吞后面的普通单词
  const re = new RegExp(
    `(^|\\s)--(?:${keys})(?:\\s*=\\s*\\S+|\\s+(?:\\d+(?:\\.\\d+)?|\\d+:\\d+))?(?=\\s|$)`,
    'gi'
  );
  return prompt.replace(re, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * 根据路径获取对象中的值
 * 支持点号与数组下标语法，例如:
 * - 'data.items[0].url'
 * - 'response[1].result.value'
 */
export function getValueByPath(obj: any, path: string): any {
  const keys = path.split('.');
  let value = obj;
  for (const key of keys) {
    if (value === null || value === undefined) {
      return undefined;
    }
    // 处理数组索引，如 'items[0]'
    if (key.includes('[') && key.includes(']')) {
      const arrayKey = key.substring(0, key.indexOf('['));
      const index = parseInt(
        key.substring(key.indexOf('[') + 1, key.indexOf(']')),
        10
      );
      value = value[arrayKey];
      if (Array.isArray(value)) {
        value = value[index];
      } else {
        return undefined;
      }
    } else {
      value = value[key as any];
    }
  }
  return value;
}

/**
 * 判断状态值是否匹配（类型安全）
 * 支持 string[] 或 number[] 的状态集合
 */
export function isStatusMatch(
  state: any,
  statusValues?: string[] | number[]
): boolean {
  if (!statusValues || statusValues.length === 0) {
    return false;
  }
  if (typeof state === 'string' && typeof statusValues[0] === 'string') {
    return (statusValues as string[]).includes(state);
  }
  if (typeof state === 'number' && typeof statusValues[0] === 'number') {
    return (statusValues as number[]).includes(state);
  }
  return false;
}

/**
 * 由纵横比与分辨率档位解析成以“宽*高”表示的 Size
 * 若未命中预设，则按比例回退：以所选档位的长边为上限（1K=1024、2K=2048、4K=4096）
 */
export function resolveImageSize(
  aspectRatio?: string,
  image_resolution?: string
): string {
  const ratio = (aspectRatio || '1:1') as typeof IMAGE_RESOLUTION_PRESETS[number]['ratio'] | string;
  const level = (image_resolution || '4K') as '1K' | '2K' | '3K' | '4K' | '8K';

  const preset = IMAGE_RESOLUTION_PRESETS.find((p) => p.ratio === ratio);
  const presetDim = (preset?.resolutions as Record<string, string> | undefined)?.[level];
  if (presetDim) {
    // 支持 '1024x576' 和 '1696*960' 两种分隔符格式
    const [w, h] = presetDim.split(/[x*]/);
    return `${w}*${h}`;
  }

  // 回退：按比例计算
  const [a, b] = (ratio || '1:1').split(':').map(Number);
  if (!isFinite(a) || !isFinite(b) || a <= 0 || b <= 0) {
    return '1024*1024';
  }
  const maxSide =
    level === '8K' ? 8192 :
    level === '4K' ? 4096 :
    level === '3K' ? 3072 :
    level === '2K' ? 2048 : 1024;
  const longest = Math.max(a, b);
  const width = Math.round((a / longest) * maxSide);
  const height = Math.round((b / longest) * maxSide);
  return `${width}*${height}`;
}

/** 历史记录 size 回填：像素尺寸写入 generated.size，节点 size 映射到模型可选值 */
export function resolveImageHistorySize(
  histSize: unknown,
  modelSizes: string[],
  preferMatch: boolean,
): { nodeSize?: string; generatedSize?: string } {
  const sizes = modelSizes.filter(Boolean);
  if (typeof histSize !== 'string' || !histSize) {
    const fallback =
      preferMatch && sizes.includes('match')
        ? 'match'
        : sizes.find((s) => s !== 'match') ?? sizes[0];
    return fallback ? { nodeSize: fallback } : {};
  }
  if (sizes.includes(histSize)) {
    return { nodeSize: histSize };
  }
  const pixelMatch = histSize.match(/^(\d+)\s*x\s*(\d+)$/i);
  if (!pixelMatch) {
    const fallback =
      preferMatch && sizes.includes('match')
        ? 'match'
        : sizes.find((s) => s !== 'match') ?? sizes[0];
    return fallback ? { nodeSize: fallback } : {};
  }
  const pw = Number(pixelMatch[1]);
  const ph = Number(pixelMatch[2]);
  if (!Number.isFinite(pw) || !Number.isFinite(ph) || pw <= 0 || ph <= 0) {
    return {};
  }
  const generatedSize = `${pw}x${ph}`;
  if (preferMatch && sizes.includes('match')) {
    return { nodeSize: 'match', generatedSize };
  }
  // 选取“比例最接近”的档位，而非精确相等：模型真实输出（如 1344x768=1.75）往往不精确等于命名比例
  // （如 16x9=1.777…），精确匹配会落空并回落到第一个选项，导致切历史时尺寸下拉跳成错误值。
  const targetRatio = pw / ph;
  let ratioMatch: string | undefined;
  let bestDiff = Infinity;
  for (const s of sizes) {
    if (s === 'match') continue;
    const mm = String(s).match(/^(\d+)\s*x\s*(\d+)$/i);
    if (!mm) continue;
    const w = Number(mm[1]);
    const h = Number(mm[2]);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) continue;
    const diff = Math.abs(w / h - targetRatio);
    if (diff < bestDiff) {
      bestDiff = diff;
      ratioMatch = s;
    }
  }
  return {
    nodeSize: ratioMatch ?? sizes.find((s) => s !== 'match') ?? sizes[0],
    generatedSize,
  };
}
